/**
 * Identity and session shapes. The auth session derives them from a token payload or the /me
 * answer; useAuth hands them to components. The wire shapes are zod schemas, parsed at
 * src/api/ — docs/rules/api_boundary.md.
 */

import { z } from 'zod';

import { listOf } from '@/api/listOf';

import {
    DeleteUserResponse as UserDeleteResponseSchema,
    MemberPermissions as MembershipPermissionsSchema,
    MeResponse as GeneratedMeResponseSchema,
    OrgRef as OrgRefSchema,
    PendingInvite as PendingInviteSchema,
    RevokeSessionsResponse as RevokeSessionsResponseSchema,
    User as UserSchema,
} from './generated/vms_database.zod';

/**
 * How the Google button delivers the credential. 'redirect' navigates the
 * current window to Google, which form-POSTs back to `login_uri`; the default
 * hands it to the callback instead.
 */
export enum GoogleUxMode {
    Popup = 'popup',
    Redirect = 'redirect',
}

/**
 * Go `auth.Claims`, the display claims of a session token: what the avatar and menu render. A
 * claim Google did not give arrives as an empty string.
 */
export const SessionClaimsSchema = z.object({
    email: z.string(),
    name: z.string(),
    picture: z.string(),
});
export type AuthUser = z.infer<typeof SessionClaimsSchema>;

/** The sign-in as a component reads it: the same object until one of its fields changes. */
export interface AuthSnapshot {
    /** A token or the sentinel cookie: the page can act as signed in now. */
    isSignedIn: boolean;
    user: AuthUser | null;
    orgId: string | null;
}

/** The part of a Web Storage that the auth session reads and writes. */
export type AuthStorage = Pick<Storage, 'getItem' | 'removeItem' | 'setItem'>;

/** The page's cookies as the auth session sees them. */
export interface SessionCookies {
    /** Every cookie that script can read, as `document.cookie` gives them. */
    read(): string;
    expireSentinel(): void;
}

export const RoleSchema = z.enum(['admin', 'viewer']);
export type Role = z.infer<typeof RoleSchema>;

export const MembershipStatusSchema = z.enum(['active', 'invited']);
export type MembershipStatus = z.infer<typeof MembershipStatusSchema>;

/** Go `model.User`. A hidden member keeps the email, the row's key; the client masks it. */
export { UserSchema };
export type User = z.infer<typeof UserSchema>;

/** Go `handlers.DeleteResponse`: `deletedOrg` when the last member left and the org went too. */
export { UserDeleteResponseSchema };
export type UserDeleteResponse = z.infer<typeof UserDeleteResponseSchema>;

/** The users list as the app holds it; `error` is set only by a failed load. */
export interface UsersSnapshot {
    users: User[];
    isLoading: boolean;
    error: Error | null;
}

export interface Membership {
    orgId: string;
    orgName?: string;
    role: Role;
    status: MembershipStatus;
    invitedBy?: string;
    invitedByName?: string;
    invitedByPicture?: string;
    createdAt: string;
}

export interface Organization {
    id: string;
    name: string;
    channelPartnerId?: string | null;
}

export { OrgRefSchema };
export type OrgRef = z.infer<typeof OrgRefSchema>;

export { PendingInviteSchema };
export type PendingInvite = z.infer<typeof PendingInviteSchema>;

/** Go `handlers.MeResponse`, as `/me`, `/setup` and `/invite/accept` answer it. */
export const MeResponseDtoSchema = GeneratedMeResponseSchema.extend({
    orgs: listOf(OrgRefSchema, 'orgs').nullable(),
    pendingInvites: listOf(PendingInviteSchema, 'pendingInvites').optional(),
});
export type MeResponseDto = z.infer<typeof MeResponseDtoSchema>;

export interface MeResponse {
    email: string;
    enrolled: boolean;
    orgs: OrgRef[];
    orgId?: string;
    orgName?: string;
    pendingInvites: PendingInvite[];
    /** Re-minted session token after a membership-growing call. */
    token?: string;
    /** The caller may administer the deployment itself (build registry). */
    platformAdmin: boolean;
}

/** The /me answer as the app holds it; `error` is set only by a failed load. */
export interface MeSnapshot {
    me: MeResponse | null;
    isLoading: boolean;
    error: Error | null;
}

/** Where a sign-in goes once /me has answered. */
export enum LoginAction {
    /** Straight into one org. */
    Single = 'single',
    /** Show the org picker. */
    Select = 'select',
    /** Offer to create the first org. */
    Setup = 'setup',
}

/** An org the login picker offers: a membership, or an invitation to one. */
export interface OrgChoice {
    orgId: string;
    orgName: string;
    role?: Role;
    invitedBy?: string;
    invitedByName?: string;
    invitedByPicture?: string;
    invited: boolean;
}

export type LoginDecision =
    | { action: LoginAction.Single; orgId: string } |
    { action: LoginAction.Select; choices: OrgChoice[] } |
    { action: LoginAction.Setup };

/** The POST /auth/session answer: the cloud-minted session token. */
export const SessionResponseSchema = z.object({
    status: z.string(),
    token: z.string().min(1),
});
export type SessionResponse = z.infer<typeof SessionResponseSchema>;

/** What a refresh of the session token ended in. */
export enum RefreshOutcome {
    /** The cookie holds a fresh token. */
    Refreshed = 'refreshed',
    /** The session ended: the page signs in again. */
    Ended = 'ended',
    /** The cloud gave no answer; the session can still be alive. */
    Failed = 'failed',
}

/** Go `handlers.refreshResponse`: the next session token and its expiry in Unix milliseconds. */
export const RefreshResponseSchema = z.object({
    token: z.string().min(1),
    expiresAt: z.number(),
});
export type RefreshResponse = z.infer<typeof RefreshResponseSchema>;

/** Go `handlers.localLoginResponse`: the token that the local-login popup gives media_server. */
export const LocalLoginResponseSchema = z.object({
    token: z.string().min(1),
});

/** Go `handlers.RevokeSessionsResponse`: how many sessions of the member ended. */
export { RevokeSessionsResponseSchema };
export type RevokeSessionsResponse = z.infer<typeof RevokeSessionsResponseSchema>;

/** The session token's expiry claim, in Unix seconds. */
export const SessionExpirySchema = z.object({
    exp: z.number(),
});

/** One member's permissions as the app holds them, with the wire schedule decoded. */
export interface MemberPermission {
    email: string;
    role: Role;
    scopeId: string | null;
    schedule: ScheduleMask | null;
}

/**
 * Every member's permissions keyed by email. `canManage` is false for a caller
 * the server refused, with `error` still null: the refusal is the answer.
 */
export interface MemberPermissionsSnapshot {
    byEmail: Record<string, MemberPermission>;
    canManage: boolean;
    isLoading: boolean;
    error: Error | null;
}

/** Stage 1 member permissions: scope null = whole org, schedule null = always. */
export { MembershipPermissionsSchema };
export type MembershipPermissions = z.infer<typeof MembershipPermissionsSchema>;

// Google Identity Services ───────────────────────────────────────────
// Google ships no types with the SDK script, so only the calls this app
// makes are declared.

export interface GisCredentialResponse {
    credential?: string;
}

export interface GisInitOptions {
    client_id: string;
    callback: (response: GisCredentialResponse) => void;
    auto_select?: boolean;
    cancel_on_tap_outside?: boolean;
    ux_mode?: GoogleUxMode;
    login_uri?: string;
}

export interface GisButtonOptions {
    type?: 'standard' | 'icon';
    theme?: string;
    size?: string;
    text?: string;
    shape?: string;
    logo_alignment?: string;
}

export interface GisIdClient {
    initialize(options: GisInitOptions): void;
    renderButton(parent: HTMLElement, options: GisButtonOptions): void;
    prompt(): void;
    cancel(): void;
    disableAutoSelect(): void;
    revoke(hint: string, done: () => void): void;
}

/**
 * A member's weekly access mask: 168 booleans, Monday 00:00 first. Null is the
 * absence of a schedule, which is not the same as an all-closed one.
 */
export type ScheduleMask = boolean[];
